import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, Star, Trophy, Users, ChevronRight, Sparkles } from 'lucide-react';

export default function Landing() {
  const features = [
    {
      icon: Leaf,
      title: 'Hands-on Learning',
      description: 'Real environmental activities that make a difference in your community.',
      color: 'bg-leaf-100 text-leaf-600',
    },
    {
      icon: Star,
      title: '36 Weeks of Fun',
      description: 'Progressive weekly challenges organized into monthly themes.',
      color: 'bg-sunshine-100 text-sunshine-600',
    },
    {
      icon: Trophy,
      title: 'Earn & Compete',
      description: 'Collect badges, earn points, and climb the leaderboard!',
      color: 'bg-orange-100 text-orange-600',
    },
    {
      icon: Users,
      title: 'Community Impact',
      description: 'Join schools across the country making environmental change.',
      color: 'bg-blue-100 text-blue-600',
    },
  ];

  const steps = [
    { number: '1', title: 'Register Your School', desc: 'Teachers sign up and add their students' },
    { number: '2', title: 'Start the Journey', desc: 'Begin with Week 1 activities' },
    { number: '3', title: 'Complete & Unlock', desc: 'Finish activities to unlock the next level' },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-leaf-600 via-leaf-500 to-leaf-700" />
        <div className="absolute inset-0 bg-leaf-pattern opacity-20" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <img
                src="/images/hero - girl, boy, bee and earth smiling and standing like heroes.png"
                alt="Leafscape Heroes"
                className="h-48 md:h-64 w-auto drop-shadow-2xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <h1 className="text-4xl md:text-6xl font-heading text-white text-shadow mb-6">
              Grow Green Champions
            </h1>

            <p className="text-xl md:text-2xl text-leaf-100 mb-8 max-w-2xl mx-auto">
              A fun, gamified journey through 36 weeks of environmental activities
              for young eco-warriors!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Link
                to="/register"
                className="btn-secondary text-lg px-8 py-4 flex items-center justify-center gap-2"
              >
                <Sparkles size={20} />
                Register School
              </Link>
              <Link
                to="/student-login"
                className="btn-ghost border-white text-white hover:bg-white hover:text-leaf-700
                           text-lg px-8 py-4 flex items-center justify-center gap-2"
              >
                Student Sign In
                <ChevronRight size={20} />
              </Link>
            </div>
            <p className="text-leaf-200 text-sm">
              Already a teacher?{' '}
              <Link to="/login" className="text-white underline hover:text-leaf-100">
                Teacher Sign In
              </Link>
            </p>
          </motion.div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              className="fill-leaf-50"
            />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-leaf-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl mb-4">Why Leafscape?</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Transform environmental education into an exciting adventure
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card text-center"
                >
                  <div className={`w-16 h-16 rounded-2xl ${feature.color} flex items-center
                                  justify-center mx-auto mb-4`}>
                    <Icon size={28} />
                  </div>
                  <h3 className="text-xl mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl mb-4">How It Works</h2>
            <p className="text-gray-600 text-lg">
              Get started in three simple steps
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-8 justify-center">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex-1 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-leaf-500 text-white text-2xl font-bold
                              flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-xl mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-leaf-600 to-leaf-800">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl text-white text-shadow mb-6">
              Ready to Grow Green Champions?
            </h2>
            <p className="text-leaf-100 text-lg mb-8 max-w-xl mx-auto">
              Join hundreds of schools already using Leafscape to teach
              environmental responsibility through play.
            </p>
            <Link
              to="/register"
              className="btn-secondary text-lg px-10 py-4 inline-flex items-center gap-2"
            >
              <Sparkles size={20} />
              Register Your School
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
